import { ChevronRight } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { PureDate } from '@/lib/absolute-datetime'
import { TimesheetsService } from '@/services/timesheets'
import type { HourType } from '@/types/hour-type'
import type { Project } from '@/types/project'
import type { Timesheet } from '@/types/timesheet'
import { SearchableSelectDialog } from './SearchableSelectDialog'

interface TimesheetLineAddDialogProps {
	timesheet: Timesheet
	open: boolean
	onOpenChange: (open: boolean) => void
	hourTypes: HourType[]
	projects: Project[]
	onAdd: (updated: Timesheet) => void
}

export function TimesheetLineAddDialog({
	timesheet,
	open,
	onOpenChange,
	hourTypes,
	projects,
	onAdd,
}: TimesheetLineAddDialogProps) {
	const [start, setStart] = useState('09:00')
	const [end, setEnd] = useState('17:00')
	const [hourTypeID, setHourTypeID] = useState('')
	const [projectID, setProjectID] = useState('')
	const [text, setText] = useState('')
	const [saving, setSaving] = useState(false)
	const [showHourTypeSearch, setShowHourTypeSearch] = useState(false)
	const [showProjectSearch, setShowProjectSearch] = useState(false)

	const selectedHourType = hourTypes.find((h) => h.id === hourTypeID)
	const selectedProject = projects.find((p) => p.id === projectID)

	const resetForm = () => {
		setStart('09:00')
		setEnd('17:00')
		setHourTypeID('')
		setProjectID('')
		setText('')
	}

	const handleSave = async () => {
		try {
			setSaving(true)

			if (!hourTypeID) {
				throw new Error('Debes seleccionar un tipo de hora')
			}
			if (!projectID) {
				throw new Error('Debes seleccionar un proyecto')
			}
			if (!start || !end) {
				throw new Error('Debes indicar hora de inicio y fin')
			}

			const baseDate = new PureDate(timesheet.date)

			const [startHoursStr, startMinutesStr] = start.split(':')
			const [endHoursStr, endMinutesStr] = end.split(':')

			if (!startHoursStr || !startMinutesStr || !endHoursStr || !endMinutesStr) {
				throw new Error('Formato de hora inválido')
			}

			const startDate = baseDate.setTime(Number(startHoursStr), Number(startMinutesStr))
			const endDate = baseDate.setTime(Number(endHoursStr), Number(endMinutesStr))

			if (startDate.compare(endDate) >= 0) {
				throw new Error('La hora de inicio debe ser anterior a la hora de fin')
			}

			if (!timesheet.company?.id || !timesheet.id) {
				throw new Error('Datos del registro incompletos')
			}

			const updated = await TimesheetsService.addTimesheetLine({
				companyID: timesheet.company.id,
				timesheetID: timesheet.id,
				hourTypeID,
				projectID,
				start: startDate.toString(),
				end: endDate.toString(),
				text: text.trim() || undefined,
			})

			onAdd(updated)
			resetForm()
			onOpenChange(false)
			toast.success('Línea añadida correctamente')
		} catch (err) {
			console.error(err)
			toast.error(err instanceof Error ? err.message : 'Error al añadir la línea')
		} finally {
			setSaving(false)
		}
	}

	return (
		<>
			<Dialog
				open={open}
				onOpenChange={(open) => {
					if (!open) resetForm()
					onOpenChange(open)
				}}
			>
				<DialogContent className="max-w-md max-h-[90vh] flex flex-col">
					<DialogHeader>
						<DialogTitle>Añadir línea</DialogTitle>
						<DialogDescription>Añade una nueva línea al registro horario</DialogDescription>
					</DialogHeader>
					<div className="space-y-4 py-4 overflow-y-auto flex-1 min-h-0">
						<div className="grid grid-cols-2 gap-4">
							<div className="space-y-2">
								<Label htmlFor="add-start">Inicio</Label>
								<Input
									id="add-start"
									type="time"
									value={start}
									onChange={(e) => setStart(e.target.value)}
									disabled={saving}
								/>
							</div>
							<div className="space-y-2">
								<Label htmlFor="add-end">Fin</Label>
								<Input
									id="add-end"
									type="time"
									value={end}
									onChange={(e) => setEnd(e.target.value)}
									disabled={saving}
								/>
							</div>
						</div>

						<div className="space-y-2">
							<Label>Tipo de hora</Label>
							<Button
								variant="outline"
								className="w-full justify-between font-normal"
								onClick={() => setShowHourTypeSearch(true)}
								disabled={saving}
							>
								<span className={selectedHourType ? '' : 'text-muted-foreground'}>
									{selectedHourType
										? `${selectedHourType.name}${selectedHourType.code ? ` (${selectedHourType.code})` : ''}`
										: 'Selecciona un tipo de hora'}
								</span>
								<ChevronRight className="h-4 w-4 shrink-0 opacity-50" />
							</Button>
						</div>
						<div className="space-y-2">
							<Label>Proyecto</Label>
							<Button
								variant="outline"
								className="w-full justify-between font-normal"
								onClick={() => setShowProjectSearch(true)}
								disabled={saving}
							>
								<span className={selectedProject ? '' : 'text-muted-foreground'}>
									{selectedProject
										? `${selectedProject.name}${selectedProject.code ? ` (${selectedProject.code})` : ''}`
										: 'Selecciona un proyecto'}
								</span>
								<ChevronRight className="h-4 w-4 shrink-0 opacity-50" />
							</Button>
						</div>
						<div className="space-y-2">
							<Label htmlFor="add-text">Comentarios</Label>
							<Textarea
								id="add-text"
								value={text}
								onChange={(e) => setText(e.target.value)}
								placeholder="Añade comentarios (opcional)"
								rows={3}
								disabled={saving}
							/>
						</div>
					</div>
					<DialogFooter className="flex-shrink-0">
						<Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
							Cancelar
						</Button>
						<Button onClick={handleSave} disabled={saving}>
							{saving ? 'Añadiendo...' : 'Añadir'}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
			<SearchableSelectDialog
				open={showHourTypeSearch}
				onOpenChange={setShowHourTypeSearch}
				title="Seleccionar tipo de hora"
				items={hourTypes}
				selectedId={hourTypeID}
				onSelect={setHourTypeID}
			/>
			<SearchableSelectDialog
				open={showProjectSearch}
				onOpenChange={setShowProjectSearch}
				title="Seleccionar proyecto"
				items={projects}
				selectedId={projectID}
				onSelect={setProjectID}
			/>
		</>
	)
}
